// EF-189: split partial Schwab brackets into a TARGET tranche + RUNNER
// tranche so the runner stop survives a take-profit fill.
//
// Schwab risk-checks each leg before activating its conditional children.
// In the pre-EF-189 nested shape `TRIGGER(LIMIT) → SINGLE replacement stop`,
// the just-canceled OCO sibling stop still counts toward open-sell-side
// exposure for a beat after the LIMIT fills — the replacement stop gets
// rejected as oversold and the position is left unprotected. The fix is
// to ship the partial bracket as two independently-tracked Schwab orders
// whose stop coverage adds up to the full position. See the 2026-05-14
// MRVL incident notes and CONTEXT.md / EF-189 for the historical record.

/**
 * Returns a v4 UUID via the platform crypto.randomUUID API. Used to tie a
 * target tranche and a runner tranche together at request time, then
 * persisted alongside each parent orderId on position_projects.schwab_order_ids
 * so the working-orders UI can recombine them later.
 */
export function makeBracketGroupId() {
  const c = globalThis.crypto || (typeof crypto !== 'undefined' ? crypto : null)
  if (c?.randomUUID) return c.randomUUID()
  // Fallback for environments without crypto.randomUUID (older Node test runners).
  const bytes = new Uint8Array(16)
  if (c?.getRandomValues) {
    c.getRandomValues(bytes)
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = [...bytes].map(b => b.toString(16).padStart(2, '0'))
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`
}

/**
 * Build the Schwab-safe order payloads for a logical bracket request.
 *
 * Behaviour matrix:
 *   - !targetPrice || targetQuantity <= 0  → one BRACKET_ENTRY, stop only
 *     (EF-182 small-position path, unchanged shape).
 *   - targetQuantity > quantity            → throws (fail-closed; no silent
 *     clamping — silent clamping bypasses the safety-layer contract).
 *   - targetQuantity === quantity          → one BRACKET_ENTRY (full-target
 *     single order).
 *   - 0 < targetQuantity < quantity        → TWO BRACKET_ENTRY payloads:
 *       1. Target tranche — quantity = targetQuantity, targetQuantity =
 *          targetQuantity, bracketRole 'TARGET', bracketGroupId set.
 *       2. Runner tranche — quantity = quantity − targetQuantity,
 *          targetQuantity = 0, targetPrice = null, bracketRole 'RUNNER',
 *          bracketGroupId shared with the target tranche.
 *
 * Every emitted payload is shape-compatible with the existing /api/oms/execute
 * BRACKET_ENTRY contract; the builder fails closed on any partial bracket so
 * the split MUST happen here, never inside the builder.
 */
export function buildSplitBracketOrders({
  accountNumber,
  symbol,
  quantity,
  entryPrice,
  stopPrice,
  targetPrice,
  targetQuantity,
  entryType,
  duration = 'GOOD_TILL_CANCEL',
  session = 'NORMAL',
  assetType = 'EQUITY',
}) {
  const baseOrder = {
    account_number: accountNumber,
    symbol: (symbol || '').toUpperCase(),
    orderType: 'BRACKET_ENTRY',
    side: 'BUY',
    price: entryPrice,
    stopPrice,
    entryType,
    duration,
    session,
    assetType,
  }

  const targetQty = Number(targetQuantity) || 0
  const qty = Number(quantity) || 0
  const hasTarget = !!targetPrice && targetQty > 0

  if (!hasTarget) {
    return [{
      ...baseOrder,
      quantity: qty,
      targetPrice: null,
      targetQuantity: 0,
      bracketRole: 'ENTRY',
      bracketGroupId: null,
    }]
  }

  if (targetQty > qty) {
    throw new Error(
      `buildSplitBracketOrders: targetQuantity (${targetQty}) cannot exceed quantity (${qty})`
    )
  }

  if (targetQty === qty) {
    return [{
      ...baseOrder,
      quantity: qty,
      targetPrice,
      targetQuantity: qty,
      bracketRole: 'ENTRY',
      bracketGroupId: null,
    }]
  }

  const bracketGroupId = makeBracketGroupId()
  return [
    {
      ...baseOrder,
      quantity: targetQty,
      targetPrice,
      targetQuantity: targetQty,
      bracketRole: 'TARGET',
      bracketGroupId,
    },
    {
      ...baseOrder,
      quantity: qty - targetQty,
      targetPrice: null,
      targetQuantity: 0,
      bracketRole: 'RUNNER',
      bracketGroupId,
    },
  ]
}

function findTrackedEntry(trackedOrderIds, orderId, accountNumber) {
  if (!Array.isArray(trackedOrderIds)) return null
  const idStr = String(orderId)
  // EF-189 v3 post-Codex hardening: key the join by (accountNumber, orderId)
  // not orderId alone. Schwab orderIds are globally unique in practice, but
  // defensively scoping by account makes the join safe against any
  // theoretical collision and matches the contract used by the rollback
  // path. Falls through to orderId-only matching for legacy tracked rows
  // (pre-EF-189) that don't carry accountNumber.
  if (accountNumber != null) {
    const acctStr = String(accountNumber)
    const exact = trackedOrderIds.find(t =>
      String(t?.orderId) === idStr && String(t?.accountNumber ?? '') === acctStr
    )
    if (exact) return exact
  }
  return trackedOrderIds.find(t => String(t?.orderId) === idStr && t?.accountNumber == null) || null
}

function findStopChildPrice(adapted) {
  if (adapted.computedStopPrice != null) return Number(adapted.computedStopPrice)
  if (Array.isArray(adapted.childOrders)) {
    const stopChild = adapted.childOrders.find(c =>
      (c.orderType === 'STOP' || c.orderType === 'STOP_LIMIT') &&
      (c.computedStopPrice != null || c.stopPrice != null),
    )
    if (stopChild) return Number(stopChild.computedStopPrice ?? stopChild.stopPrice)
  }
  if (adapted.stopPrice != null) return Number(adapted.stopPrice)
  return null
}

function findTargetChild(adapted) {
  if (!Array.isArray(adapted.childOrders)) return null
  return adapted.childOrders.find(c =>
    c.orderType === 'LIMIT' &&
    c.price != null &&
    (c.side || '').includes('SELL'),
  ) || null
}

/**
 * Join target + runner pairs back into a single synthetic SPLIT_BRACKET row
 * for display + cancel. Orders without a bracketGroupId pass through unchanged
 * so legacy / externally-placed brackets keep working during the transition.
 */
export function groupSplitBracketOrders(adaptedOrders, trackedOrderIds) {
  if (!Array.isArray(adaptedOrders) || adaptedOrders.length === 0) return []

  // Build per-order metadata index from the tracked-id list. Key by
  // (accountNumber, orderId) so two accounts cannot cross-wire metadata
  // even in the theoretical case of an orderId collision across accounts.
  const metaByOrderKey = new Map()
  const keyFor = (accountNumber, orderId) => `${accountNumber ?? ''}|${orderId}`
  for (const adapted of adaptedOrders) {
    const tracked = findTrackedEntry(trackedOrderIds, adapted.orderId, adapted.accountNumber)
    if (tracked && tracked.bracketGroupId) {
      metaByOrderKey.set(keyFor(adapted.accountNumber, adapted.orderId), tracked)
    }
  }

  // Pair orders by group id.
  const byGroup = new Map()
  for (const adapted of adaptedOrders) {
    const meta = metaByOrderKey.get(keyFor(adapted.accountNumber, adapted.orderId))
    if (!meta) continue
    if (!byGroup.has(meta.bracketGroupId)) byGroup.set(meta.bracketGroupId, [])
    byGroup.get(meta.bracketGroupId).push({ adapted, meta })
  }

  const collapsedIds = new Set()
  const collapsedByFirstId = new Map()

  for (const [, entries] of byGroup) {
    if (entries.length !== 2) continue
    const target = entries.find(e => e.meta.role === 'TARGET_ENTRY')
    const runner = entries.find(e => e.meta.role === 'RUNNER_ENTRY')
    if (!target || !runner) continue

    const targetParent = target.adapted
    const runnerParent = runner.adapted

    const targetStop = findStopChildPrice(targetParent)
    const runnerStop = findStopChildPrice(runnerParent)
    if (targetStop != null && runnerStop != null && targetStop !== runnerStop) {
      console.warn('[bracketOrderGroups] split bracket has mismatched stop prices', {
        groupId: target.meta.bracketGroupId, targetStop, runnerStop,
      })
    }

    const targetLimit = findTargetChild(targetParent)
    const combined = {
      ...targetParent,
      orderType: 'SPLIT_BRACKET',
      groupKind: 'SPLIT_BRACKET',
      groupedOrders: [targetParent, runnerParent],
      groupedOrderIds: [String(targetParent.orderId), String(runnerParent.orderId)],
      quantity: (Number(targetParent.quantity) || 0) + (Number(runnerParent.quantity) || 0),
      targetQuantity: targetLimit ? Number(targetLimit.quantity) || 0 : 0,
      runnerQuantity: Number(runnerParent.quantity) || 0,
      targetPrice: targetLimit ? Number(targetLimit.price) : null,
      computedStopPrice: targetStop ?? runnerStop ?? null,
      // Preserve targetParent.stopPrice (typically null — bracket parents
      // carry the protective stop inside childOrders, not as a top-level
      // field). Setting stopPrice here would change row-label heuristics
      // downstream and disagree with how single bracket parents render.
      cancelable: !!targetParent.cancelable && !!runnerParent.cancelable,
      bracketGroupId: target.meta.bracketGroupId,
    }

    collapsedIds.add(keyFor(targetParent.accountNumber, targetParent.orderId))
    collapsedIds.add(keyFor(runnerParent.accountNumber, runnerParent.orderId))
    collapsedByFirstId.set(keyFor(targetParent.accountNumber, targetParent.orderId), combined)
  }

  const out = []
  for (const adapted of adaptedOrders) {
    const key = keyFor(adapted.accountNumber, adapted.orderId)
    if (collapsedByFirstId.has(key)) {
      out.push(collapsedByFirstId.get(key))
      continue
    }
    if (collapsedIds.has(key)) continue
    out.push(adapted)
  }
  return out
}

// ── EF-189 v3: split-group rollback helpers ─────────────────────────────
// A split group is identified by a shared `bracketGroupId` across two or
// more per-order PLACE results. Partial failure = at least one success +
// at least one failure inside the same group. The successful sibling is
// a live orphan at Schwab that must be cancelled before finalising
// results, otherwise the position is left half-protected.

function groupResultsByBracketGroup(results) {
  if (!Array.isArray(results)) return new Map()
  const groups = new Map()
  for (const r of results) {
    const gid = r?.bracketGroupId
    if (!gid) continue
    if (!groups.has(gid)) groups.set(gid, [])
    groups.get(gid).push(r)
  }
  return groups
}

/**
 * Returns true when any bracketGroupId in `results` has a mixed success
 * state (at least one success and at least one failure). Uniform-success
 * and uniform-failure groups both return false. Non-array / empty input
 * returns false. Results without a bracketGroupId are ignored.
 */
export function splitGroupHasPartialFailure(results) {
  const groups = groupResultsByBracketGroup(results)
  for (const [, members] of groups) {
    let anySuccess = false
    let anyFailure = false
    for (const m of members) {
      if (m?.success === true) anySuccess = true
      else anyFailure = true
      if (anySuccess && anyFailure) return true
    }
  }
  return false
}

/**
 * For each result that succeeded inside a group with at least one failure,
 * returns { account_number, symbol, orderId, bracketGroupId, bracketRole }.
 * These are the orphan-cancel targets. Returns [] when no partial-failure
 * groups exist or every group is uniformly successful / uniformly failed.
 */
export function findSuccessfulOrphanSiblings(results) {
  const groups = groupResultsByBracketGroup(results)
  const orphans = []
  for (const [, members] of groups) {
    const anyFailure = members.some(m => m?.success !== true)
    const anySuccess = members.some(m => m?.success === true)
    if (!anyFailure || !anySuccess) continue
    for (const m of members) {
      if (m?.success !== true) continue
      if (m.orderId == null) continue
      orphans.push({
        account_number: m.account_number ?? m.accountNumber ?? null,
        symbol: m.symbol ?? null,
        orderId: m.orderId,
        bracketGroupId: m.bracketGroupId,
        bracketRole: m.bracketRole,
      })
    }
  }
  return orphans
}

/**
 * EF-190: returns true when any of the position's tracked entries carries a
 * split-bracket marker — TARGET_ENTRY role, RUNNER_ENTRY role, or any
 * non-null bracketGroupId. Used by ActionPanel to disable legacy quick
 * actions (Move Stop / Take 1/3 Profit / Close Full Position) until proper
 * split-bracket support ships. A partial state (only TARGET or only RUNNER)
 * still returns true — half-states are not safe enough for the legacy
 * payloads either.
 *
 * Accepts the same `projects` array PositionWorkspace passes to ActionPanel.
 * Returns false for empty input, null, missing schwab_order_ids, or all
 * tracked entries with role 'ENTRY' and null bracketGroupId.
 */
export function isSplitBracketPosition(projects) {
  if (!Array.isArray(projects) || projects.length === 0) return false
  for (const project of projects) {
    const tracked = project?.schwab_order_ids
    if (!Array.isArray(tracked) || tracked.length === 0) continue
    for (const entry of tracked) {
      if (!entry || typeof entry !== 'object') continue
      if (entry.role === 'TARGET_ENTRY') return true
      if (entry.role === 'RUNNER_ENTRY') return true
      if (entry.bracketGroupId != null && entry.bracketGroupId !== '') return true
    }
  }
  return false
}

// EF-195: terminal-status set for the live-target check. Anything else
// (including a missing or blank string) counts as non-terminal — the
// orders feed strips most terminal rows server-side, so any LIMIT child
// we still see is overwhelmingly a live working target. Conservative
// against missing data; the false-positive surface is bounded by the
// per-account ≥2 same-price-stop requirement.
const EF195_TERMINAL_ORDER_STATUSES = new Set([
  'FILLED', 'CANCELED', 'EXPIRED', 'REJECTED', 'REPLACED',
])

/**
 * EF-195: conservative broker-shape detector that runs alongside
 * isSplitBracketPosition (the EF-190 metadata path). Closes the hole
 * exposed by the 2026-05-27 DOCN regression — legacy / imported positions
 * that present the split-bracket shape at Schwab but are missing the
 * Erevis-side TARGET_ENTRY / RUNNER_ENTRY / bracketGroupId tags.
 *
 * Returns true only when ALL three conditions trip for at least one
 * account in `accountNumbers`:
 *   1. ≥2 SELL STOP legs at the same .toFixed(2) stop price within that
 *      account (sourced from the caller-passed `stopOrders` array, which
 *      ActionPanel computes via getStopOrdersForSymbol).
 *   2. At least one bracket parent (`isBracket === true`) for that
 *      (symbol, account) whose direct childOrders contain a LIMIT child
 *      with side containing 'SELL', non-null price, and a non-terminal
 *      status. A missing / blank status is treated as non-terminal —
 *      only the explicit terminal set (FILLED / CANCELED / EXPIRED /
 *      REJECTED / REPLACED) fails the live-target check.
 *   3. Both signals hit the SAME account (cross-account mirroring of
 *      stops does not count — that is the normal multi-account entry
 *      pattern and must not trip the guard).
 *
 * Standalone SELL LIMIT orders are NOT target evidence. Manual standalone
 * limit-sell take-profits are a legitimate user workflow; only a
 * bracket-parent's LIMIT child counts here.
 */
export function hasBrokerShapeSplitBracket({ symbol, accountNumbers, rawOrders, stopOrders } = {}) {
  if (!symbol) return false
  if (!Array.isArray(accountNumbers) || accountNumbers.length === 0) return false
  if (!Array.isArray(rawOrders) || rawOrders.length === 0) return false
  if (!Array.isArray(stopOrders) || stopOrders.length === 0) return false

  const upperSymbol = String(symbol).toUpperCase()
  const accountSet = new Set(accountNumbers.map(String))

  // Blank / missing status counts as non-terminal (live); only the explicit
  // terminal set fails the live-target check.
  const isNonTerminalStatus = (statusRaw) => {
    const status = String(statusRaw ?? '').toUpperCase()
    if (status === '') return true
    return !EF195_TERMINAL_ORDER_STATUSES.has(status)
  }

  // (1) Same-account same-price SELL STOP count per (account, priceKey).
  const stopCountByAcctPrice = new Map()
  for (const stop of stopOrders) {
    if (!stop) continue
    const acct = String(stop.accountNumber ?? '')
    if (!accountSet.has(acct)) continue
    const instruction = stop.instruction ?? stop.side ?? ''
    if (!String(instruction).includes('SELL')) continue
    const numPrice = Number(stop.stopPrice)
    if (!Number.isFinite(numPrice)) continue
    const priceKey = numPrice.toFixed(2)
    const k = `${acct}|${priceKey}`
    stopCountByAcctPrice.set(k, (stopCountByAcctPrice.get(k) || 0) + 1)
  }

  // Per-account set of accounts that have at least one same-price SELL STOP cluster (≥2).
  const accountsWithStopCluster = new Set()
  for (const [k, n] of stopCountByAcctPrice) {
    if (n >= 2) accountsWithStopCluster.add(k.split('|')[0])
  }
  if (accountsWithStopCluster.size === 0) return false

  // (2) Per-account set of accounts with live bracket-target evidence for
  // this symbol. Two data shapes can carry that evidence:
  //   • Shape A — enriched parent: an order with isBracket===true whose
  //     childOrders include a non-terminal SELL LIMIT child. This is the
  //     shape /api/schwab/orders returns (orderAdapter.js sets isBracket
  //     from hasChildren and flattens childOrders).
  //   • Shape B — promoted child: a top-level order with
  //     promotedFromBracket===true that itself is a non-terminal SELL
  //     LIMIT. /api/schwab/refresh-position promotes bracket children
  //     to top-level rows with promotedFromBracket=true (line 69), so
  //     when the live target arrives through that endpoint it never
  //     appears as a parent's childOrders entry. Without Shape B the
  //     2026-05-27 DOCN-style untagged broker shape would still escape
  //     the guard — exactly the regression EF-195 closes.
  // Standalone SELL LIMITs (promotedFromBracket false/undefined) remain
  // explicitly NOT evidence so manual limit-sell workflows are preserved.
  const accountsWithLiveTarget = new Set()
  for (const order of rawOrders) {
    if (!order || typeof order !== 'object') continue
    const orderSymbol = String(order.symbol ?? '').toUpperCase()
    if (orderSymbol !== upperSymbol) continue
    const acct = String(order.accountNumber ?? '')
    if (!accountSet.has(acct)) continue

    // Shape A — enriched bracket parent with childOrders LIMIT entry.
    if (order.isBracket === true) {
      const children = Array.isArray(order.childOrders) ? order.childOrders : []
      const hasLiveLimit = children.some(child => {
        if (!child || child.orderType !== 'LIMIT') return false
        const childSide = String(child.side ?? '')
        if (!childSide.includes('SELL')) return false
        if (child.price == null) return false
        return isNonTerminalStatus(child.status)
      })
      if (hasLiveLimit) {
        accountsWithLiveTarget.add(acct)
        continue
      }
    }

    // Shape B — promoted bracket child surfaced as a top-level LIMIT row.
    // Gated on promotedFromBracket so standalone manual SELL LIMITs are
    // never treated as target evidence.
    if (
      order.promotedFromBracket === true &&
      order.orderType === 'LIMIT' &&
      String(order.side ?? '').includes('SELL') &&
      order.price != null &&
      isNonTerminalStatus(order.status)
    ) {
      accountsWithLiveTarget.add(acct)
    }
  }
  if (accountsWithLiveTarget.size === 0) return false

  // (3) The two signals must intersect on at least one account.
  for (const acct of accountsWithStopCluster) {
    if (accountsWithLiveTarget.has(acct)) return true
  }
  return false
}

/**
 * EF-196-PRE-BLOCKER (Slice 0 of OMS safety arc): predicate that returns
 * true when the working-orders snapshot describes a split-exit topology
 * that NEITHER the EF-190 metadata path NOR the EF-195 broker-shape
 * detector can admit. The canonical example is the 2026-05-27 DOCN shape:
 * one top-level standalone SELL LIMIT (`promotedFromBracket !== true`,
 * `parentOrderId == null`, `isBracket !== true`) plus two or more
 * same-account active SELL stops. EF-195 cannot admit that because its
 * Shape A requires an enriched bracket parent and its Shape B requires
 * `promotedFromBracket === true` on the LIMIT row. EF-195b is the slice
 * that will eventually admit this as Shape C; until then the unsupported
 * topology trips the same disable surface as a known split bracket so
 * Move Stop / Take 1/3 Profit / Close Full Position cannot silently fire
 * against a Schwab-unsafe payload assumption.
 *
 * Returns true only when ALL of:
 *   1. ≥2 active SELL stops in the same account (sourced from `stopOrders`,
 *      which ActionPanel computes via getStopOrdersForSymbol — by construction
 *      a list of SELL-side STOP / STOP_LIMIT orders pre-filtered for active
 *      status; the SELL-side accessor below is defensive against fixtures
 *      that supply mixed sides).
 *   2. ≥1 standalone SELL LIMIT in the same account that is NOT a bracket
 *      child (promotedFromBracket !== true, parentOrderId == null,
 *      isBracket !== true) and is non-terminal.
 *   3. (1) and (2) intersect on the same account.
 *   4. hasBrokerShapeSplitBracket(...) returned false — supported split
 *      shapes defer to EF-195 / its own admission path.
 */
export function isUnsupportedSplitExitTopology({ symbol, accountNumbers, rawOrders, stopOrders } = {}) {
  if (!symbol) return false
  if (!Array.isArray(accountNumbers) || accountNumbers.length === 0) return false
  if (!Array.isArray(rawOrders) || rawOrders.length === 0) return false
  if (!Array.isArray(stopOrders) || stopOrders.length === 0) return false

  const upperSymbol = String(symbol).toUpperCase()
  const accountSet = new Set(accountNumbers.map(String))

  const isNonTerminalStatus = (statusRaw) => {
    const status = String(statusRaw ?? '').toUpperCase()
    if (status === '') return true
    return !EF195_TERMINAL_ORDER_STATUSES.has(status)
  }

  // Shared SELL-side accessor — mirrors hasBrokerShapeSplitBracket:
  //   refresh-position rows expose `side`, /api/schwab/orders rows expose
  //   `instruction`. Treat either, plus SELL_SHORT, as sell-side evidence.
  const isSellSide = (entry) =>
    String(entry?.instruction ?? entry?.side ?? '').includes('SELL')

  // (1) Per-account count of active SELL stops.
  const sellStopCountByAcct = new Map()
  for (const stop of stopOrders) {
    if (!stop) continue
    const acct = String(stop.accountNumber ?? '')
    if (!accountSet.has(acct)) continue
    if (!isSellSide(stop)) continue
    if (!isNonTerminalStatus(stop.status)) continue
    if (!Number.isFinite(Number(stop.stopPrice))) continue
    sellStopCountByAcct.set(acct, (sellStopCountByAcct.get(acct) || 0) + 1)
  }
  const accountsWithMultipleSellStops = new Set()
  for (const [acct, n] of sellStopCountByAcct) {
    if (n > 1) accountsWithMultipleSellStops.add(acct)
  }
  if (accountsWithMultipleSellStops.size === 0) return false

  // (2) Per-account presence of a standalone SELL LIMIT.
  const accountsWithStandaloneSellLimit = new Set()
  for (const order of rawOrders) {
    if (!order || typeof order !== 'object') continue
    if (order.orderType !== 'LIMIT') continue
    if (!isSellSide(order)) continue
    const orderSymbol = String(order.symbol ?? '').toUpperCase()
    if (orderSymbol !== upperSymbol) continue
    const acct = String(order.accountNumber ?? '')
    if (!accountSet.has(acct)) continue
    if (order.promotedFromBracket === true) continue
    if (order.parentOrderId != null) continue
    if (order.isBracket === true) continue
    if (!isNonTerminalStatus(order.status)) continue
    accountsWithStandaloneSellLimit.add(acct)
  }
  if (accountsWithStandaloneSellLimit.size === 0) return false

  // (3) Intersection — same account in both sets.
  let overlap = false
  for (const acct of accountsWithMultipleSellStops) {
    if (accountsWithStandaloneSellLimit.has(acct)) { overlap = true; break }
  }
  if (!overlap) return false

  // (4) EF-195 admission gate — defer if the broker-shape detector already
  // admits this as Shape A or Shape B.
  if (hasBrokerShapeSplitBracket({ symbol, accountNumbers, rawOrders, stopOrders })) {
    return false
  }

  return true
}
